import { Config } from '../config/config.js';

import { LocalStorageSettingsGateway } from '../gateways/local-storage-settings-gateway.js';

import { LocalStorageAuthGateway } from '../gateways/local-storage-auth-gateway.js';

import { DebugGateway } from '../gateways/debug-gateway.js';

import { AuthGateway } from '../gateways/auth-gateway.js';
import { createAuthSessionClient } from '../utils/create-auth-session-client.js';
import { createUiHelper } from '../utils/create-ui-helper.js';

import { TeamsGateway } from '../gateways/teams-gateway.js';

import { CoachGateway } from '../gateways/coach-gateway.js';

import { EventGateway } from '../gateways/event-gateway.js';

import { WorkbenchGateway } from '../gateways/workbench-gateway.js';

import { ChatsGateway } from '../gateways/chats-gateway.js';
import { IntelligenceGateway } from '../gateways/intelligence-gateway.js';
import { IntelligenceService } from '../services/intelligence-service.js';

import { ProjectsGateway } from '../gateways/projects-gateway.js';
import { ShareGateway } from '../gateways/share-gateway.js';
import { SearchGateway } from '../gateways/search-gateway.js';

import { SettingsService } from '../services/settings-service.js';

import { PromptLibraryService } from '../services/prompt-library-service.js';

import { AuthService } from '../services/auth-service.js';

import { CoachService } from '../services/coach-service.js';

import { EventService } from '../services/event-service.js';
import { ShareService } from '../services/share-service.js';
import { SearchService } from '../services/search-service.js';

import { WorkbenchService } from '../services/workbench-service.js';
import { InactivityMonitor } from '../services/inactivity-monitor.js';

import { ChatsService } from '../services/chats-service.js';

import { ProjectsService } from '../services/projects-service.js';

import { TeamsService } from '../services/teams-service.js';

import { TeamsPresenter } from '../presenters/teams-presenter.js';
import { SearchPresenter } from '../presenters/search-presenter.js';

import { TeamsController } from '../controllers/teams-controller.js';
import { SearchController } from '../controllers/search-controller.js';

import { TeamsView } from '../views/teams-view.js';
import { resolveUiHelper } from '../utils/resolve-ui-helper.js';
import { SearchView } from '../views/search-view.js';

import { WorkbenchHistory } from './workbench-history.js';

import { buildPrecisionPrompt } from '../domain/coach/build-precision-prompt.js';

import { buildEventPrecisionPrompt, buildEventFollowupPrecisionPrompt } from '../domain/event/build-event-prompts.js';

export function createContainer(overrides = {}) {

  const config = overrides.config || new Config();

  const debugModule =

    overrides.debugModule || (typeof window !== 'undefined' ? window.Debug : null);

  const authModule =

    overrides.authModule || (typeof window !== 'undefined' ? window.Auth : null);

  const apiModule =

    overrides.apiModule || null;

  const fetchImpl =
    overrides.fetch ||
    (typeof window !== 'undefined' && typeof window.fetch === 'function' ? window.fetch.bind(window) : null);

  const postForm =
    overrides.postForm ||
    (fetchImpl ? (url, fields, files) => submitFormRequest({ fetch: fetchImpl, url, fields, files }) : null);

  let authPersistenceGateway = overrides.authPersistenceGateway || null;

  if (overrides.authGateway instanceof AuthGateway) {

    authPersistenceGateway = authPersistenceGateway || overrides.authGateway.persistence || null;

  } else if (overrides.authGateway) {

    authPersistenceGateway = authPersistenceGateway || overrides.authGateway;

  }

  if (!authPersistenceGateway) {

    authPersistenceGateway = new LocalStorageAuthGateway({ storage: overrides.authStorage });

  }

  const authSessionClient =
    overrides.authSessionClient ||
    createAuthSessionClient({
      fetchImpl,
      getApiBase: () => {
        try {
          return config.apiBase;
        } catch (error) {
          return null;
        }
      },
      getToken: () =>
        authPersistenceGateway && typeof authPersistenceGateway.getToken === 'function'
          ? authPersistenceGateway.getToken()
          : null,
    });

  const authGateway =

    overrides.authGateway instanceof AuthGateway

      ? overrides.authGateway

      : new AuthGateway({
          ambientAuth: authModule,
          persistenceGateway: authPersistenceGateway,
          sessionClient: authSessionClient,
        });

  const debugGateway =

    overrides.debugGateway instanceof DebugGateway

      ? overrides.debugGateway

      : new DebugGateway({ debug: overrides.debugGateway || debugModule });

  const resolveGatewayDebugInstance = (gateway) => {
    if (!gateway) {
      return null;
    }
    try {
      if (typeof gateway.get === 'function') {
        const fromGetter = gateway.get();
        if (fromGetter) {
          return fromGetter;
        }
      }
      if ('instance' in gateway && gateway.instance) {
        return gateway.instance;
      }
    } catch (error) {
      return null;
    }
    return null;
  };

  const containerDebugProvider =
    typeof overrides.debugProvider === 'function'
      ? overrides.debugProvider
      : () =>
          resolveGatewayDebugInstance(overrides.debugGateway || debugGateway) ||
          (typeof window !== 'undefined' ? window.Debug || null : null);

  const teamsGateway = overrides.teamsGateway || new TeamsGateway({ fetch: fetchImpl, apiBase: config.apiBase });
  const teamGateway = overrides.teamGateway || teamsGateway;
  const coachGateway = overrides.coachGateway || (postForm ? new CoachGateway({ postForm, apiBase: config.apiBase }) : null);

  const gateways = {

    settings:

      overrides.settingsGateway ||

      new LocalStorageSettingsGateway({ storage: overrides.settingsStorage }),

    auth: authGateway,

    authPersistence: authPersistenceGateway,

    team: teamGateway,
    teams: teamsGateway,

    coach: coachGateway,
    event:

      overrides.eventGateway ||

      (postForm ? new EventGateway({ postForm, apiBase: config.apiBase }) : null),

    workbench:

      overrides.workbenchGateway ||

      (postForm ? new WorkbenchGateway({ postForm, apiBase: config.apiBase }) : null),

    chats:

      overrides.chatsGateway ||

      (fetchImpl ? new ChatsGateway({ fetch: fetchImpl, apiBase: config.apiBase }) : null),

    projects:

      overrides.projectsGateway || new ProjectsGateway({ fetch: fetchImpl, apiBase: config.apiBase }),

    share:
      overrides.shareGateway ||
      (fetchImpl ? new ShareGateway({ fetch: fetchImpl, apiBase: config.apiBase }) : null),
    search:
      overrides.searchGateway ||
      (fetchImpl ? new SearchGateway({ fetch: fetchImpl, apiBase: config.apiBase }) : null),
    intelligence:
      overrides.intelligenceGateway ||
      (fetchImpl ? new IntelligenceGateway({ fetch: fetchImpl, apiBase: config.apiBase }) : null),

    debug: debugGateway,

  };

  const services = {
    settings: overrides.settingsService || new SettingsService(gateways.settings),
    auth: overrides.authService || new AuthService(gateways.auth),
    promptLibrary: overrides.promptLibraryService || null,
    teams: overrides.teamsService || null,
    chats: overrides.chatsService || null,
    share: overrides.shareService || null,
    search: overrides.searchService || null,
  };

  const presenters = {
    teams: overrides.teamsPresenter || null,
    search: overrides.searchPresenter || null,
  };

  const views = {
    teams: overrides.teamsView || null,
    search: overrides.searchView || null,
  };

  const controllers = {
    teams: overrides.teamsController || null,
    search: overrides.searchController || null,
  };

  const hasInactivityMonitorOverride = Object.prototype.hasOwnProperty.call(overrides, 'inactivityMonitor');

  const inactivityMonitor =

    hasInactivityMonitorOverride

      ? overrides.inactivityMonitor

      : new InactivityMonitor({

      documentRef: typeof window !== 'undefined' ? window.document : null,

      timeoutMs: overrides.inactivityTimeoutMs,

      getAuthState: () => {

        try {

          return services.auth ? services.auth.get() : { isLoggedIn: false };

        } catch (error) {

          return { isLoggedIn: false };

        }

      },

      onTimeout: () => {

        try {

          const authService = services.auth;

          if (authService && typeof authService.logout === 'function') {

            authService.logout();

          } else if (gateways.auth && typeof gateways.auth.clear === 'function') {

            gateways.auth.clear();

          }

        } finally {

          if (typeof window !== 'undefined' && window.location) {

            window.location.href = '/register.html';

          }

        }

      },

    });

  if (inactivityMonitor && gateways.auth && typeof gateways.auth.setInactivityMonitor === 'function') {

    gateways.auth.setInactivityMonitor(inactivityMonitor);

  }

  services.inactivityMonitor = inactivityMonitor || null;

  const documentRef = overrides.document || (typeof window !== 'undefined' ? window.document : null);
  const uiHelper = overrides.uiHelper || (documentRef ? createUiHelper({ document: documentRef }) : null);

  const helpers = {

    addUserMessage: overrides.addUserMessage || null,

    promptLibrary: overrides.promptLibrary || null,

    toolHistory: overrides.toolHistory || new WorkbenchHistory(),
    ui: uiHelper,

    debugProvider: containerDebugProvider,
  };

  if (overrides.coachService) {

    services.coach = overrides.coachService;

  }

  if (overrides.eventService) {

    services.event = overrides.eventService;

  }

  if (overrides.workbenchService) {

    services.workbench = overrides.workbenchService;

  }

  if (overrides.projectsService) {

    services.projects = overrides.projectsService;

  }

    const viewFactories = {
    teams: () => {
      if (views.teams) {
        return views.teams;
      }

      const ViewCtor =
        overrides.TeamsView ||
        (typeof window !== 'undefined'
          ? window.AgileProViews?.TeamsView || TeamsView
          : TeamsView);

      if (typeof ViewCtor !== 'function') {
        return null;
      }

      const doc = overrides.document || (typeof window !== 'undefined' ? window.document : null);
      const uiHelper = resolveUiHelper() || {};
      const toastFn =
        typeof overrides.toast === 'function'
          ? overrides.toast
          : typeof uiHelper.showToast === 'function'
          ? uiHelper.showToast
          : typeof window !== 'undefined' && typeof window.toast === 'function'
          ? window.toast
          : () => {};
      const notify =
        typeof overrides.teamNotify === 'function'
          ? overrides.teamNotify
          : (message, type, title, duration) => {
              try {
                toastFn(message, type, title, duration);
              } catch (error) {
                // ignore toast errors
              }
            };

      views.teams = new ViewCtor({
        document: doc,
        toast: toastFn,
        notify,
      });

      return views.teams;
    },
    search: () => {
      if (views.search) {
        return views.search;
      }

      const ViewCtor =
        overrides.SearchView ||
        (typeof window !== 'undefined'
          ? window.AgileProViews?.SearchView || SearchView
          : SearchView);

      if (typeof ViewCtor !== 'function') {
        return null;
      }

      const doc = overrides.document || (typeof window !== 'undefined' ? window.document : null);
      const uiHelper = resolveUiHelper() || {};
      const modalFactory =
        typeof overrides.makeSearchModal === 'function'
          ? overrides.makeSearchModal
          : typeof uiHelper.makeModal === 'function'
          ? uiHelper.makeModal
          : typeof window !== 'undefined' && typeof window.makeModal === 'function'
          ? window.makeModal
          : null;
      const escapeFn =
        typeof overrides.escape === 'function'
          ? overrides.escape
          : typeof uiHelper.esc === 'function'
          ? uiHelper.esc
          : typeof window !== 'undefined' && typeof window.esc === 'function'
          ? window.esc
          : (value) => value;

      views.search = new ViewCtor({
        document: doc,
        modalFactory,
        escape: escapeFn,
      });

      return views.search;
    },
  };

  const presenterFactories = {
    teams: () => {
      if (presenters.teams) {
        return presenters.teams;
      }

      const PresenterCtor =
        overrides.TeamsPresenter ||
        (typeof window !== 'undefined'
          ? window.AgileProPresenters?.TeamsPresenter || TeamsPresenter
          : TeamsPresenter);

      if (typeof PresenterCtor !== 'function') {
        return null;
      }

      presenters.teams = new PresenterCtor();
      return presenters.teams;
    },
    search: () => {
      if (presenters.search) {
        return presenters.search;
      }

      const PresenterCtor =
        overrides.SearchPresenter ||
        (typeof window !== 'undefined'
          ? window.AgileProPresenters?.SearchPresenter || SearchPresenter
          : SearchPresenter);

      if (typeof PresenterCtor !== 'function') {
        return null;
      }

      const service =
        services.search ||
        (typeof serviceFactories.search === 'function' ? serviceFactories.search() : null);

      if (!service) {
        return null;
      }

      presenters.search = new PresenterCtor({ searchService: service });
      return presenters.search;
    },
  };

const serviceFactories = {

    promptLibrary: () => {

      if (services.promptLibrary) {

        return services.promptLibrary;

      }

      const settingsService = services.settings;

      if (!settingsService || typeof settingsService.get !== 'function' || typeof settingsService.save !== 'function') {

        return null;

      }

      let settings;

      try {

        settings = settingsService.get() || {};

      } catch (error) {

        return null;

      }

      if (!settings || typeof settings !== 'object') {

        settings = {};

      }

      services.promptLibrary = new PromptLibraryService({

        settings,

        saveSettings: (next) => settingsService.save(next),

      });

      return services.promptLibrary || null;

    },

    coach: () => {

      if (services.coach) {

        return services.coach;

      }

      const CoachServiceCtor =

        overrides.CoachService ||

        (typeof window !== 'undefined'

          ? window.AgileProServices?.CoachService || CoachService

          : CoachService);

      const promptBuilder =

        overrides.promptBuilder ||

        (typeof window !== 'undefined'

          ? window.AgileProDomain?.coach?.buildPrecisionPrompt || buildPrecisionPrompt

          : buildPrecisionPrompt);

      if (CoachServiceCtor && typeof promptBuilder === 'function' && gateways.coach) {

        services.coach = new CoachServiceCtor({

          promptBuilder,

          coachGateway: gateways.coach,

          teamGateway: gateways.team,

          debugGateway: overrides.coachDebugGateway || gateways.debug || null,

        });

      }

      return services.coach || null;

    },

    event: () => {

      if (services.event) {

        return services.event;

      }

      const EventServiceCtor =

        overrides.EventService ||

        (typeof window !== 'undefined'

          ? window.AgileProServices?.EventService || EventService

          : EventService);

      const planBuilder =

        overrides.eventPromptBuilder ||

        (typeof window !== 'undefined'

          ? window.AgileProDomain?.event?.buildEventPrecisionPrompt || buildEventPrecisionPrompt

          : buildEventPrecisionPrompt);

      const followupBuilder =

        overrides.eventFollowupPromptBuilder ||

        (typeof window !== 'undefined'

          ? window.AgileProDomain?.event?.buildEventFollowupPrecisionPrompt || buildEventFollowupPrecisionPrompt

          : buildEventFollowupPrecisionPrompt);

      if (

        EventServiceCtor &&

        typeof planBuilder === 'function' &&

        typeof followupBuilder === 'function' &&

        gateways.event

      ) {

        services.event = new EventServiceCtor({

          promptBuilder: planBuilder,

          followupPromptBuilder: followupBuilder,

          eventGateway: gateways.event,

          teamGateway: gateways.team,

        });

      }

      return services.event || null;

    },

    teams: () => {

      if (services.teams) {

        return services.teams;

      }

      const TeamsServiceCtor =

        overrides.TeamsService ||

        (typeof window !== 'undefined'

          ? window.AgileProServices?.TeamsService || TeamsService

          : TeamsService);

      const gateway = gateways.teams;

      if (TeamsServiceCtor && gateway) {

        services.teams = new TeamsServiceCtor({

          teamsGateway: gateway,

        });

      }

      return services.teams || null;

    },

    workbench: () => {

      if (services.workbench) {

        return services.workbench;

      }

      const WorkbenchServiceCtor =

        overrides.WorkbenchService ||

        (typeof window !== 'undefined'

          ? window.AgileProServices?.WorkbenchService || WorkbenchService

          : WorkbenchService);

      if (WorkbenchServiceCtor && gateways.workbench) {

        services.workbench = new WorkbenchServiceCtor({

          workbenchGateway: gateways.workbench,

        });

      }

      return services.workbench || null;

    },

    chats: () => {

      if (services.chats) {

        return services.chats;

      }

      const ChatsServiceCtor =

        overrides.ChatsService ||

        (typeof window !== 'undefined'

          ? window.AgileProServices?.ChatsService || ChatsService

          : ChatsService);

      const gateway = gateways.chats;

      if (ChatsServiceCtor && gateway) {

        services.chats = new ChatsServiceCtor({

          chatsGateway: gateway,

          projectsService: services.projects || null,

        });

      }

      return services.chats || null;

    },

    share: () => {
      if (services.share) {
        return services.share;
      }

      const ShareServiceCtor =
        overrides.ShareService ||
        (typeof window !== 'undefined'
          ? window.AgileProServices?.ShareService || ShareService
          : ShareService);

      const gateway = gateways.share;

      if (ShareServiceCtor && gateway) {
        services.share = new ShareServiceCtor({
          gateway,
        });
      }

      return services.share || null;
    },
    search: () => {
      if (services.search) {
        return services.search;
      }

      const SearchServiceCtor =
        overrides.SearchService ||
        (typeof window !== 'undefined'
          ? window.AgileProServices?.SearchService || SearchService
          : SearchService);

      const gateway = gateways.search;

      if (SearchServiceCtor && gateway) {
        const options = { gateway };
        if (typeof overrides.searchFallback === 'function') {
          options.fallbackSearch = overrides.searchFallback;
        }
        if (typeof overrides.searchNormaliseResult === 'function') {
          options.normaliseResult = overrides.searchNormaliseResult;
        }
        services.search = new SearchServiceCtor(options);
      }

      return services.search || null;
    },

    projects: () => {

      if (services.projects) {

        return services.projects;

      }

      const ProjectsServiceCtor =

        overrides.ProjectsService ||

        (typeof window !== 'undefined'

          ? window.AgileProServices?.ProjectsService || ProjectsService

          : ProjectsService);

      const gateway = gateways.projects;

      if (ProjectsServiceCtor && gateway) {

        services.projects = new ProjectsServiceCtor({
          projectsGateway: gateway,
          settingsService: services.settings || null,
          documentRef: typeof window !== 'undefined' ? window.document : null,
        });

      }

      return services.projects || null;

    },

    intelligence: () => {

      if (services.intelligence) {

        return services.intelligence;

      }

      const IntelligenceServiceCtor =
        overrides.IntelligenceService ||
        (typeof window !== 'undefined'
          ? window.AgileProServices?.IntelligenceService || IntelligenceService
          : IntelligenceService);

      const gateway = gateways.intelligence;

      if (typeof IntelligenceServiceCtor === 'function' && gateway) {

        services.intelligence = new IntelligenceServiceCtor({
          intelligenceGateway: gateway,
          debugGateway,
        });

      }

      return services.intelligence || null;

    },

  };

  const controllerFactories = {
    teams: () => {
      if (controllers.teams) {
        return controllers.teams;
      }

      const ControllerCtor =
        overrides.TeamsController ||
        (typeof window !== 'undefined'
          ? window.AgileProControllers?.TeamsController || TeamsController
          : TeamsController);

      if (typeof ControllerCtor !== 'function') {
        return null;
      }

      const service = services.teams || serviceFactories.teams();
      const presenter = presenters.teams || presenterFactories.teams();
      const view = views.teams || viewFactories.teams();

      if (!service || !presenter || !view) {
        return null;
      }

      const onTeamsUpdated =
        typeof overrides.onTeamsUpdated === 'function'
          ? overrides.onTeamsUpdated
          : () => {
              if (typeof window !== 'undefined' && window.document) {
                try {
                  window.document.dispatchEvent(new CustomEvent('teams:updated'));
                } catch (error) {
                  // ignore dispatch failures
                }
              }
            };

      const onTeamCreated =
        typeof overrides.onTeamCreated === 'function'
          ? overrides.onTeamCreated
          : async ({ teamId } = {}) => {
              if (typeof window !== 'undefined' && typeof window.refreshTeams === 'function') {
                await window.refreshTeams({ teamId, keepValue: true });
              }
            };

      const setLastTeamSelection =
        typeof overrides.setLastTeamSelection === 'function'
          ? overrides.setLastTeamSelection
          : (selection) => {
              if (typeof window !== 'undefined') {
                window.__agpLastTeamSelection = selection;
              }
            };

      const getLastTeamSelection =
        typeof overrides.getLastTeamSelection === 'function'
          ? overrides.getLastTeamSelection
          : () => (typeof window !== 'undefined' ? window.__agpLastTeamSelection || null : null);

      const notify =
        typeof overrides.teamNotify === 'function'
          ? overrides.teamNotify
          : (message, type, title, duration) => {
              if (typeof view.notify === 'function') {
                view.notify(message, type, title, duration);
              } else {
                try {
                  const toastFn =
                    typeof overrides.toast === 'function'
                      ? overrides.toast
                      : typeof window !== 'undefined' && typeof window.toast === 'function'
                      ? window.toast
                      : null;
                  if (typeof toastFn === 'function') {
                    toastFn(message, type, title, duration);
                  }
                } catch (error) {
                  // ignore toast errors
                }
              }
            };

      controllers.teams = new ControllerCtor({
        view,
        service,
        presenter,
        notify,
        onTeamsUpdated,
        onTeamCreated,
        setLastTeamSelection,
        getLastTeamSelection,
      });

      controllers.teams.init();

      return controllers.teams;
    },
    search: () => {
      if (controllers.search) {
        return controllers.search;
      }

      const ControllerCtor =
        overrides.SearchController ||
        (typeof window !== 'undefined'
          ? window.AgileProControllers?.SearchController || SearchController
          : SearchController);

      if (typeof ControllerCtor !== 'function') {
        return null;
      }

      const presenter = presenters.search || presenterFactories.search();
      const view = views.search || viewFactories.search();

      if (!presenter || !view) {
        return null;
      }

      const controllerOptions = {
        view,
        presenter,
      };

      if (typeof overrides.searchDebounceMs === 'number') {
        controllerOptions.debounceMs = overrides.searchDebounceMs;
      }

      controllerOptions.openChat =
        typeof overrides.openSearchChat === 'function'
          ? overrides.openSearchChat
          : (chatId) => {
              if (!chatId || typeof window === 'undefined' || !window.document) {
                return;
              }
              try {
                window.document.dispatchEvent(new CustomEvent('chat:open', { detail: { id: chatId } }));
              } catch (error) {
                // ignore dispatch failures
              }
            };

      controllers.search = new ControllerCtor(controllerOptions);

      return controllers.search;
    },
  };

  return {

    config,

    gateways,

    services,

    helpers,

    getHelper(name) {

      return helpers[name] || null;

    },

    registerHelper(name, value) {

      if (!name) {

        return null;

      }

      helpers[name] = value;

      return helpers[name];

    },

    getView(name) {
      if (views[name]) {
        return views[name];
      }
      const factory = viewFactories[name];
      const created = typeof factory === 'function' ? factory() : null;
      if (created) {
        views[name] = created;
      }
      return created;
    },

    getPresenter(name) {
      if (presenters[name]) {
        return presenters[name];
      }
      const factory = presenterFactories[name];
      const created = typeof factory === 'function' ? factory() : null;
      if (created) {
        presenters[name] = created;
      }
      return created;
    },

    getController(name) {
      if (controllers[name]) {
        return controllers[name];
      }
      const factory = controllerFactories[name];
      const created = typeof factory === 'function' ? factory() : null;
      if (created) {
        controllers[name] = created;
      }
      return created;
    },

    getService(name) {

      if (services[name]) {

        return services[name];

      }

      const factory = serviceFactories[name];

      return typeof factory === 'function' ? factory() : null;

    },

    getGateway(name) {

      return gateways[name];

    },

  };

}
